import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { proceduresQuery } from '../api/softsee'
import { moduleOf, shellQuery } from '../api/shell'
import { Content } from '../ui/Shell'
import { Spinner } from '../ui/Spinner'
import { usePageStyles } from '../ui/usePageStyles'
/* Capul de ecran al setarilor noi (`h4` + `label.color`), aceeasi piesa ca pe
   celelalte ecrane din zona de setari. Piesa sta in `ui/SettingsParts`. */
import { SettingsHead } from '../ui/SettingsParts'

/**
 * Softsee::procedures - registrul de citit. Scrierea ramane in Settings.
 *
 * `adm.css` e foaia din care sunt facute benzile, cardurile si cifrele -
 * ecranul vechi o aducea prin `loadPlugins = ['adm']`. Aici era legata doar
 * `procedures.css`, si atunci printr-un `<link>` scris in mijlocul paginii,
 * fara versiune: pagina se desena pe un fundal alb, fara benzi si fara tonuri.
 */

const STYLES = ['css/adm.css', 'css/procedures.css']

function Empty({ what, where }: { what: string; where: string }) {
  return (
    <p className="proc-empty">
      <i className="far fa-folder-open"></i> Nothing written yet. {what}{' '}
      <a href="/app/settings/opteemum" target="_blank" rel="noopener noreferrer">
        {where}
      </a>
      .
    </p>
  )
}

/**
 * Casuta de cautare a UNEI sectiuni, pe linia titlului ei.
 *
 * Sta intr-un `adm-section-tools` - piesa benzii, din `adm.css`, care duce la
 * dreapta ce i se da (`margin-left: auto`), aceeasi pe care o folosesc filele
 * din Maintenance. Marginea ei din dreapta e chiar marginea benzii, deci
 * casutele celor trei sectiuni cad una sub alta fara sa le asezam noi.
 *
 * Nu e `SoftseeListChrome`: aceea e chenarul unui tabel DataTables care isi
 * cere randurile de la server, cu paginare si cu numarul de randuri. Aici sunt
 * trei liste deja aduse - cel mult 200 de randuri fiecare, `LIMIT 200` in
 * `DbalPagesSource::procedures` - fiecare cu FELUL ei de rand, si filtrarea se
 * face in browser. Un chenar de tabel pus pe o banda colorata ar fi adus cu el
 * paginarea si numaratoarea, adica desenul unei liste, pe un registru care nu
 * e lista.
 */
function SectionFind({
  id,
  what,
  value,
  onChange,
  shown,
  total,
}: {
  id: string
  /** Ce se cauta - intra in eticheta citita cu voce tare. */
  what: string
  value: string
  onChange(next: string): void
  shown: number
  total: number
}) {
  return (
    <span className="adm-section-tools proc-find">
      <i className="fas fa-search" aria-hidden="true"></i>
      <label className="sr-only" htmlFor={id}>
        Search {what}
      </label>
      <input
        id={id}
        type="search"
        className="form-control input-sm"
        placeholder="Search..."
        autoComplete="off"
        spellCheck={false}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onChange('')
        }}
      />
      {value.trim() !== '' && (
        <span className="adm-count">
          {shown} of {total} shown
        </span>
      )}
    </span>
  )
}

/**
 * Randul cautat se cunoaste dupa CE SE VEDE in el - coloanele desenate, nu
 * id-urile si nici caile ascunse in raspuns - si fara sa conteze literele mari
 * sau spatiile de la capetele cautarii.
 */
function matches(needle: string, ...cells: (string | number)[]): boolean {
  if (needle === '') return true

  return cells.join(' | ').toLowerCase().includes(needle)
}

/** Sectiunea care n-a gasit nimic o spune, in locul unei benzi cu nimic sub ea. */
function NoMatch({ needle }: { needle: string }) {
  return (
    <p className="proc-empty proc-nomatch">
      <i className="fas fa-search"></i> Nothing here matches &ldquo;{needle}&rdquo;. Clear the box to see everything again.
    </p>
  )
}

export function ProceduresPage() {
  const shell = useQuery(shellQuery(moduleOf(typeof window === 'undefined' ? '' : window.location.pathname)))
  usePageStyles(STYLES, shell.data?.version ?? '')
  const query = useQuery(proceduresQuery())

  /* Cate o cautare de sectiune, in trei stari separate: sectiunile se citesc
     una cate una, iar o singura casuta pentru toate ar fi ascuns randuri din
     doua sectiuni la care omul nici nu se uita. */
  const [findActivation, setFindActivation] = useState('')
  const [findRemoval, setFindRemoval] = useState('')
  const [findConfiguration, setFindConfiguration] = useState('')

  if (query.isPending) {
    return (
      <Content>
        <Spinner />
      </Content>
    )
  }
  if (query.isError) {
    return (
      <Content>
        <div className="alert alert-danger">{(query.error as Error).message}</div>
      </Content>
    )
  }

  const { activation, removal, configuration, missing } = query.data

  const needleActivation = findActivation.trim().toLowerCase()
  const needleRemoval = findRemoval.trim().toLowerCase()
  const needleConfiguration = findConfiguration.trim().toLowerCase()

  const activationRows = activation.filter((row) => matches(needleActivation, row.name, row.version, row.kill, row.line))
  const removalRows = removal.filter((row) => matches(needleRemoval, row.name, row.version, row.line))
  const configurationRows = configuration.filter((row) =>
    matches(needleConfiguration, row.name, row.type, row.category, row.note, row.script),
  )

  /*
   * Fara `tone`: tonurile erau ale casetelor de tablou, iar cifrele sunt acum
   * pastilele consolei, care isi iau culoarea din foaia zonei.
   *
   * Cifrele numara TOT registrul, nu ce a ramas dupa cautare. Ele stau in capul
   * paginii, deasupra celor trei sectiuni, si raspund la "cat e scris aici" -
   * o intrebare despre ce are firma, nu despre ce am tastat eu adineauri. O
   * pastila care ar scadea la fiecare tasta ar arata ca niste proceduri s-au
   * sters. Cate au ramas dupa cautare se vede oricum, langa casuta care le-a
   * ascuns, si numai cat timp cautarea e pornita.
   */
  const stats = [
    { value: String(activation.length), label: 'activation' },
    { value: String(removal.length), label: 'uninstall' },
    { value: String(configuration.length), label: 'configuration' },
  ]

  return (
    /*
     * Fragment, nu `Content`: ecranul isi scrie SINGUR `<div id="content">` mai
     * jos, iar `Content` e chiar `<div id="content">`. Puse unul in altul,
     * pagina avea acelasi id de doua ori.
     */
    <>
      <div id="content" className="proc">
        {/*
         * Capul zonei, ca pe celelalte sapte ecrane din Softsee: `h4` de 22px si
         * subtitlul sub el. Inainte era banda de tablou de modul - iconita
         * intr-un patrat colorat, `h3`, si trei casete de cifre - adica desenul
         * tablourilor, nu al setarilor, pe un ecran care se deschide DINAUNTRUL
         * setarilor.
         *
         * Cifrele nu se pierd: raman in cap, ca pastilele consolei.
         */}
        <SettingsHead
          strong="Procedures"
          subtitle="fully custom activation, uninstall and configuration - and exactly what runs on the machine"
          tools={stats.map((s) => (
            <span key={s.label} className="label" style={{ marginLeft: 6 }}>
              {s.value} {s.label}
            </span>
          ))}
        />
        {missing.length > 0 && (
          <div className="proc-warn">
            <i className="fas fa-database"></i> Part of this is not installed on this instance ({missing.join(', ')}), so its section stays empty. That is a missing module, not a missing feature.
          </div>
        )}
        <p className="proc-lead">
          This is the register. Procedures are written in <strong>Settings &gt; Opteemum</strong> and run from a device&apos;s own tabs - one place to write, one place to run, and this one to see what exists.
        </p>
        <div className="adm-section adm-section-green-mode">
          <h4>
            <i className="fas fa-play"></i> Activation
          </h4>
          <span className="adm-section-note">the customer&apos;s own packages, with the switches somebody chose for them</span>
          {/* Pe o sectiune goala nu se deseneaza casuta: n-ar avea ce cauta in
              ea, si ar sta pe linia unui titlu sub care scrie "Nothing written
              yet". */}
          {activation.length > 0 && (
            <SectionFind
              id="procFindActivation"
              what="activation procedures"
              value={findActivation}
              onChange={setFindActivation}
              shown={activationRows.length}
              total={activation.length}
            />
          )}
        </div>
        <div className="adm-card">
          {activation.length === 0 ? (
            <Empty what="Packages are uploaded in" where="Settings > Opteemum > Software custom kits" />
          ) : activationRows.length === 0 ? (
            <NoMatch needle={findActivation.trim()} />
          ) : (
            <table className="proc-table">
              <thead>
                <tr>
                  <th>Procedure</th>
                  <th>Version</th>
                  <th>Stops first</th>
                  <th>What runs on the machine</th>
                </tr>
              </thead>
              <tbody>
                {activationRows.map((row, i) => (
                  <tr key={i}>
                    <td className="proc-name">{row.name}</td>
                    <td className="proc-dim">{row.version}</td>
                    <td>{row.kill !== '' ? <span className="proc-kill">{row.kill}</span> : <span className="proc-dim">&mdash;</span>}</td>
                    <td>{row.line !== '' ? <code className="proc-line">{row.line}</code> : <span className="proc-dim">no package file on this entry</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="adm-section adm-section-red-intense">
          <h4>
            <i className="fas fa-trash-alt"></i> Uninstall
          </h4>
          <span className="adm-section-note">the same packages, the other way round - one register, so the two cannot drift apart</span>
          {removal.length > 0 && (
            <SectionFind
              id="procFindUninstall"
              what="uninstall procedures"
              value={findRemoval}
              onChange={setFindRemoval}
              shown={removalRows.length}
              total={removal.length}
            />
          )}
        </div>
        <div className="adm-card">
          {removal.length === 0 ? (
            <Empty what="There is nothing to remove until a package exists in" where="Settings > Opteemum > Software custom kits" />
          ) : removalRows.length === 0 ? (
            <NoMatch needle={findRemoval.trim()} />
          ) : (
            <table className="proc-table">
              <thead>
                <tr>
                  <th>Procedure</th>
                  <th>Version</th>
                  <th>What runs on the machine</th>
                </tr>
              </thead>
              <tbody>
                {removalRows.map((row, i) => (
                  <tr key={i}>
                    <td className="proc-name">{row.name}</td>
                    <td className="proc-dim">{row.version}</td>
                    <td>
                      <code className="proc-line">{row.line}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="adm-section adm-section-blue-hoki">
          <h4>
            <i className="fas fa-terminal"></i> Configuration
          </h4>
          <span className="adm-section-note">scripts written from scratch - this is &quot;fully custom&quot; in the strongest sense of the words</span>
          {configuration.length > 0 && (
            <SectionFind
              id="procFindConfiguration"
              what="configuration commands"
              value={findConfiguration}
              onChange={setFindConfiguration}
              shown={configurationRows.length}
              total={configuration.length}
            />
          )}
        </div>
        <div className="adm-card">
          {configuration.length === 0 ? (
            <Empty what="Commands are written in" where="Settings > Opteemum > Commands" />
          ) : configurationRows.length === 0 ? (
            <NoMatch needle={findConfiguration.trim()} />
          ) : (
            configurationRows.map((row, i) => (
              <div className="proc-cmd" key={i}>
                <div className="proc-cmd-head">
                  <span className="proc-name">{row.name}</span>
                  {row.type !== '' && <span className="proc-tag">{row.type}</span>}
                  {row.category !== '' && <span className="proc-tag proc-tag-grey">{row.category}</span>}
                  {row.asks && <span className="proc-tag proc-tag-amber">asks for a value</span>}
                  {row.timeout > 0 && <span className="proc-dim">gives up after {row.timeout}s</span>}
                </div>
                {row.note !== '' && <p className="proc-note">{row.note}</p>}
                {row.script !== '' ? <pre className="proc-script">{row.script}</pre> : <p className="proc-dim">Nothing written under this one - it would run empty.</p>}
              </div>
            ))
          )}
        </div>
        <div className="adm-section adm-section-grey">
          <h4>
            <i className="fas fa-long-arrow-alt-right"></i> Where each one is run
          </h4>
        </div>
        <div className="adm-card proc-where">
          <ul>
            <li>
              <strong>Activation</strong> &mdash; a device&apos;s <em>Install Applications</em> tab, or in bulk from <em>Scheduled installs</em> for the whole estate at once.
            </li>
            <li>
              <strong>Uninstall</strong> &mdash; a device&apos;s <em>Uninstall Applications</em> tab.
            </li>
            <li>
              <strong>Configuration</strong> &mdash; a device&apos;s <em>Remote Command Prompt</em>, where the command is picked by name and the machine answers back.
            </li>
          </ul>
        </div>
      </div>
    </>
  )
}
